/**
 * NavigationHandler - Handles navigation events and auth state cleanup
 * 
 * Manages redirects based on user authentication state, plan level,
 * and various checkout/verification flows.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { auth } from '../../../firebase/firebase';
import AuthLoadingScreen from '../AuthLoadingScreen';

function NavigationHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isTransitioning, resetTransitioning, clearLastError, user } = useAuth();
  const [pendingRedirect, setPendingRedirect] = useState(false);
  const prevUserPlanLevelRef = useRef(null);
  const prevUserRef = useRef(null);
  
  // Clean up any stale redirect data on component mount
  useEffect(() => {
    const staleRedirectData = sessionStorage.getItem('auth_redirect_state');
    if (staleRedirectData) {
      console.log('NavigationHandler: Clearing stale auth_redirect_state on mount');
      sessionStorage.removeItem('auth_redirect_state');
    }
  }, []);
  
  // Store the previous plan level to detect downgrades
  useEffect(() => {
    if (user?.planLevel) {
      prevUserPlanLevelRef.current = user.planLevel;
    }
    if (user) {
      prevUserRef.current = user;
    }
  }, [user?.planLevel, user]);
  
  // Debug log when location changes
  useEffect(() => {
    console.log('Navigation occurred to:', location.pathname);
    
    // Check if we're in the middle of signing out or cancelling subscription
    const isSigningOut = sessionStorage.getItem('isSigningOut') === 'true';
    if (isSigningOut) {
      if (location.pathname === '/' || location.pathname === '/app/account/pricing') {
        console.log('Sign out or subscription cancel navigation complete, clearing flag');
        sessionStorage.removeItem('isSigningOut');
      } else if (location.pathname.startsWith('/app')) {
        console.log('Still on app page after sign out/cancellation, forcing redirect');
        navigate('/', { replace: true });
      }
    }
  }, [location.pathname, navigate]);
  
  // Reset auth states when navigation occurs
  useEffect(() => {
    if (isTransitioning) {
      console.log('Auth is in transitioning state, will reset soon...');
      // Increase delay to ensure navigation has fully completed
      const timer = setTimeout(() => {
        resetTransitioning();
        clearLastError();
        console.log('Navigation complete, reset transitioning state');
      }, 2000); // Increase to 2 seconds to ensure navigation completes
      
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isTransitioning, resetTransitioning, clearLastError]);
  
  // Check for stored redirect data from SignIn component
  useEffect(() => {
    // Lock out redirects during email verification in-progress only
    const evInProgress = sessionStorage.getItem('email_verification_in_progress') === 'true';
    if (evInProgress) {
      console.log('NavigationHandler: Email verification lock active, skipping redirect logic');
      return;
    }
    // Skip all redirects for Facebook ad page
    if (location.pathname === '/ad/facebook') {
      console.log('On Facebook ad page, skipping NavigationHandler redirects');
      return;
    }
    
    // Check if account deletion is in progress - if so, don't process any redirects
    if (sessionStorage.getItem('accountDeletionInProgress') === 'true') {
      console.log('Account deletion in progress, skipping all NavigationHandler redirects');
      return;
    }
    
    // Check if checkout success is in progress - if so, don't process any redirects
    if (sessionStorage.getItem('checkout_success_in_progress') === 'true') {
      console.log('Checkout success in progress, skipping NavigationHandler redirects');
      return;
    }
    
    // CRITICAL FIX: Check if we're on the success page but have no authenticated user
    // This could happen if someone deleted their account or logged out and stale data is redirecting them
    // BUT: Allow success page access if there are valid Stripe session parameters
    if (location.pathname.startsWith('/success') && !user) {
      // Check for valid Stripe session parameters
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      const hasStripeData = sessionId || 
                           localStorage.getItem('stripeSessionId') ||
                           localStorage.getItem('stripe_session_id') ||
                           localStorage.getItem('stripe_checkout_initiated') === 'true';
      
      if (!hasStripeData) {
        console.log('User on success page but not authenticated and no Stripe data - likely stale data, redirecting to home');
        // Clear any stale Stripe data that might be causing the redirect
        localStorage.removeItem('stripeSessionId');
        localStorage.removeItem('stripe_session_id');
        localStorage.removeItem('stripe_checkout_initiated');
        localStorage.removeItem('stripe_checkout_auth_state');
        localStorage.removeItem('stripe_checkout_return_path');
        localStorage.removeItem('post_login_redirect');
        localStorage.removeItem('post_login_session_id');
        sessionStorage.removeItem('in_stripe_checkout_flow');
        sessionStorage.removeItem('stripe_checkout_cancelled');
        
        // Redirect to home page
        navigate('/', { replace: true });
        return;
      } else {
        console.log('User on success page without auth but has valid Stripe data - allowing CheckoutSuccess to handle auth restoration');
        return;
      }
    }
    
    // Check for immediate downgrade scenario first
    const isImmediateDowngrade = sessionStorage.getItem('immediate_downgrade') === 'true';
    if (isImmediateDowngrade && location.pathname.startsWith('/app')) {
      console.log('Immediate downgrade detected! Forcing redirect to pricing page...');
      sessionStorage.removeItem('immediate_downgrade');
      
      // Set pending redirect to prevent flash of incorrect content
      setPendingRedirect(true);
      
      // Immediate redirect without delay
      navigate('/pricing', { replace: true });
      setTimeout(() => setPendingRedirect(false), 100);
      return;
    }
    
    const redirectDataJson = sessionStorage.getItem('auth_redirect_state');
    
    if (redirectDataJson) {
      try {
        const redirectData = JSON.parse(redirectDataJson);
        console.log('Found stored redirect data:', redirectData);
        
        // CRITICAL FIX: Always clear the data immediately to prevent multiple redirects
        sessionStorage.removeItem('auth_redirect_state');
        
        // CRITICAL FIX: Validate redirect data against current user's actual plan level
        // Only use stored redirect data if it's consistent with current user state
        let shouldUseStoredData = true;
        let actualIsPremiumOrPro = false;
        
        if (user) {
          actualIsPremiumOrPro = user.planLevel === 'premium' || user.isEnterprise;
          const storedIsPremiumOrPro = 
            redirectData.isPremiumOrPro || 
            redirectData.planLevel === 'premium' || 
            redirectData.isEnterprise ||
            redirectData.currentPlanLevel === 'premium';
          
          // If stored data conflicts with actual user state, ignore it
          if (storedIsPremiumOrPro !== actualIsPremiumOrPro) {
            console.log('Stored redirect data conflicts with actual user state, ignoring it:', {
              storedIsPremiumOrPro,
              actualIsPremiumOrPro,
              userPlanLevel: user.planLevel,
              storedData: redirectData
            });
            shouldUseStoredData = false;
          }
          
          // ADDITIONAL CHECK: If redirect data is older than 30 seconds, consider it stale
          const dataAge = Date.now() - (redirectData.timestamp || 0);
          if (dataAge > 30000) {
            console.log('Stored redirect data is stale (older than 30s), ignoring it:', {
              dataAge: dataAge + 'ms',
              timestamp: redirectData.timestamp
            });
            shouldUseStoredData = false;
          }
        } else {
          // If no user but we have redirect data, it's likely stale
          console.log('No authenticated user but found redirect data - likely stale, ignoring it');
          shouldUseStoredData = false;
        }
        
        if (!shouldUseStoredData) {
          console.log('Skipping redirect due to data inconsistency or staleness');
          return;
        }
        
        // Enhanced check to ensure we correctly determine premium status
        // CRITICAL: Use actual user state as source of truth, not stored data
        const isPremiumOrPro = actualIsPremiumOrPro;
        
        console.log('Enhanced plan level check from redirect data:', {
          originalIsPremiumOrPro: redirectData.isPremiumOrPro,
          actualIsPremiumOrPro,
          finalIsPremiumOrPro: isPremiumOrPro,
          planLevel: redirectData.planLevel,
          currentPlanLevel: redirectData.currentPlanLevel,
          userPlanLevel: user?.planLevel
        });
        
        // Don't do unnecessary redirects if we're already where we should be
        if (isPremiumOrPro && location.pathname.startsWith('/app')) {
          console.log('Already on app page, no redirect needed');
          return;
        }
        
        if (!isPremiumOrPro && location.pathname === '/pricing') {
          console.log('Already on pricing page, no redirect needed');
          return;
        }
        
        // Set pending redirect to prevent flash of incorrect content
        setPendingRedirect(true);
        
        // Use timeout to ensure navigation happens after UI updates
        const redirectTimeout = isPremiumOrPro ? 100 : 100;
        setTimeout(() => {
          if (isPremiumOrPro) {
            console.log('Redirecting to app based on actual user plan level');
            navigate('/app/', { replace: true });
          } else {
            console.log('Redirecting to pricing based on actual user plan level');
            navigate('/pricing', { replace: true });
          }
          // Reset pending state after navigation
          setPendingRedirect(false);
        }, redirectTimeout);
      } catch (error) {
        console.error('Error parsing redirect data:', error);
        sessionStorage.removeItem('auth_redirect_state');
      }
    }
  }, [location.pathname, navigate, user]);
  
  // Detect plan level downgrade (premium -> basic) and redirect if needed
  useEffect(() => {
    if (user && prevUserPlanLevelRef.current) {
      const prevPlanLevel = prevUserPlanLevelRef.current;
      const currentPlanLevel = user.planLevel;
      
      // Check if user was downgraded from premium to basic
      const wasDowngraded = 
        (prevPlanLevel === 'premium' || user?.isEnterprise) && 
        (currentPlanLevel === 'basic' || currentPlanLevel === 'standard');
      
      if (wasDowngraded && location.pathname.startsWith('/app')) {
        console.log('Plan downgrade detected! Redirecting from app to pricing page...', {
          previousPlan: prevPlanLevel,
          currentPlan: currentPlanLevel,
          path: location.pathname
        });
        
        // Set pending redirect to prevent flash of incorrect content
        setPendingRedirect(true);
        
        // Short delay to ensure UI updates properly
        setTimeout(() => {
          navigate('/pricing', { replace: true });
          setPendingRedirect(false);
        }, 100);
      }
    }
  }, [user, navigate, location.pathname]);
  
  // Improved redirection logic after login - checks subscription level
  useEffect(() => {
    // Lock out redirects during email verification in-progress only
    const evInProgress = sessionStorage.getItem('email_verification_in_progress') === 'true';
    if (evInProgress) {
      console.log('NavigationHandler: Email verification lock active, skipping login redirect logic');
      return;
    }
    // Skip all redirects for Facebook ad page
    if (location.pathname === '/ad/facebook') {
      console.log('On Facebook ad page, skipping login redirect logic');
      return;
    }
    
    // Check if account deletion is in progress - if so, don't process any redirects
    if (sessionStorage.getItem('accountDeletionInProgress') === 'true') {
      console.log('Account deletion in progress, skipping login redirect logic');
      return;
    }
    
    // CRITICAL FIX: Add a debounce mechanism to prevent rapid successive redirects
    const now = Date.now();
    const lastRedirectTime = sessionStorage.getItem('last_redirect_time');
    if (lastRedirectTime && (now - parseInt(lastRedirectTime)) < 1000) {
      console.log('NavigationHandler: Skipping redirect - too soon after last redirect');
      return;
    }
    
    // Check if we just logged in and are on login page
    if (user && (location.pathname === '/login' || location.pathname === '/signin')) {
      console.log('User is authenticated but still on login page, determining redirect target...', {
        userId: user?.uid,
        planLevel: user?.planLevel,
        subscriptionStatus: user?.subscriptionStatus,
        requiresEmailVerification: user?.requiresEmailVerification,
        emailVerified: user?.emailVerified
      });
      
      // CRITICAL FIX: Check if there's a Google sign-in error flag - if so, don't redirect
      const googleSignInError = sessionStorage.getItem('google_signin_error') === 'true';
      if (googleSignInError) {
        console.log("NavigationHandler: Google sign-in error detected, staying on sign-in page");
        return;
      }
      
      // ADDITIONAL CHECK: Check for any auth errors that should keep user on sign-in page
      const hasAuthError = localStorage.getItem('refrral_auth_error') !== null ||
                          localStorage.getItem('auth_error_timestamp') !== null;
      if (hasAuthError) {
        console.log("NavigationHandler: Auth error detected, staying on sign-in page");
        return;
      }
      
      // Check if user needs email verification first
      const isEnterpriseUser = user?.isEnterpriseUser === true || user?.isEnterprise === true;
      
      if (isEnterpriseUser && auth.currentUser && !auth.currentUser.emailVerified) {
        console.log('NavigationHandler: User needs email verification, redirecting to verify-email page');
        setPendingRedirect(true);
        navigate('/verify-email', { replace: true });
        setTimeout(() => setPendingRedirect(false), 500);
        return;
      }
      
      // Record this redirect attempt
      sessionStorage.setItem('last_redirect_time', now.toString());
      
      // Set pending redirect to prevent flash of incorrect content
      setPendingRedirect(true);
      
      // More robust check for premium subscription
      const isPremiumOrPro = 
        (user?.planLevel === 'premium' || user?.isEnterprise) || 
        (user?.subscriptionStatus?.currentPlanLevel === 'premium');
      
      // Only redirect if we're not already where we should be
      if (isPremiumOrPro && !location.pathname.startsWith('/app')) {
        console.log('Redirecting premium user to dashboard...');
        setPendingRedirect(true);
        navigate('/app/', { replace: true });
        setTimeout(() => {
          setPendingRedirect(false);
          // Clear Google sign-in flag after successful navigation
          sessionStorage.removeItem('googleSignInInProgress');
          // Clear Google sign-up flag after successful navigation
          sessionStorage.removeItem('googleSignUpInProgress');
        }, 500);
      } else if (!isPremiumOrPro && !isEnterpriseUser && location.pathname !== '/pricing' && !location.pathname.startsWith('/success')) {
        // IMPORTANT: Only redirect non-enterprise basic users to public pricing
        // Enterprise users NEVER go to pricing page
        console.log('Redirecting basic non-enterprise user to public pricing...');
        navigate('/pricing', { replace: true });
        // CRITICAL FIX: The redirect is initiated, so we MUST clear the pending state.
        setPendingRedirect(false);
        // Clear Google sign-in flags immediately
        sessionStorage.removeItem('googleSignInInProgress');
        sessionStorage.removeItem('googleSignUpInProgress');
      } else if (isEnterpriseUser && !isPremiumOrPro && !location.pathname.startsWith('/app')) {
        // Enterprise users without premium/pro plans still go to app, not pricing
        console.log('Redirecting enterprise user to app...');
        setPendingRedirect(true);
        navigate('/app/', { replace: true });
        setTimeout(() => {
          setPendingRedirect(false);
          sessionStorage.removeItem('googleSignInInProgress');
          sessionStorage.removeItem('googleSignUpInProgress');
        }, 500);
      } else {
        console.log('NavigationHandler: User already at correct destination, no redirect needed');
        // Clear Google sign-in flag if we're already at the right place
        sessionStorage.removeItem('googleSignInInProgress');
        sessionStorage.removeItem('googleSignUpInProgress');
      }
    }
    
    // Check for the just logged in flag that might be set by SignIn component
    const justLoggedIn = sessionStorage.getItem('justLoggedIn') === 'true';
    if (justLoggedIn && user) {
      console.log('Just logged in flag detected, ensuring proper navigation...', {
        userId: user?.uid,
        planLevel: user?.planLevel,
        subscriptionStatus: user?.subscriptionStatus,
        requiresEmailVerification: user?.requiresEmailVerification,
        emailVerified: user?.emailVerified
      });
      
      // CRITICAL FIX: Check if there's a Google sign-in error flag - if so, don't redirect
      const googleSignInError = sessionStorage.getItem('google_signin_error') === 'true';
      if (googleSignInError) {
        console.log("NavigationHandler: Google sign-in error detected in justLoggedIn, clearing flag and staying on sign-in page");
        sessionStorage.removeItem('justLoggedIn');
        return;
      }
      
      // Check if we're coming from a checkout flow - if so, skip redirect as CheckoutSuccess will handle navigation
      const isComingFromCheckout = localStorage.getItem('stripe_checkout_initiated') === 'true' ||
                                  localStorage.getItem('stripeSessionId') ||
                                  sessionStorage.getItem('in_stripe_checkout_flow') === 'true' ||
                                  window.location.search.includes('session_id=');
      
      if (isComingFromCheckout) {
        console.log('Coming from checkout flow, skipping automatic redirect - CheckoutSuccess will handle navigation');
        // Clear the flag but don't redirect
        sessionStorage.removeItem('justLoggedIn');
        return;
      }
      
      // Check if user needs email verification first
      const isEnterpriseUser = user?.isEnterpriseUser === true || user?.isEnterprise === true;
      
      if (isEnterpriseUser && auth.currentUser && !auth.currentUser.emailVerified) {
        console.log('NavigationHandler: User needs email verification (justLoggedIn), redirecting to verify-email page');
        sessionStorage.removeItem('justLoggedIn');
        setPendingRedirect(true);
        navigate('/verify-email', { replace: true });
        setTimeout(() => setPendingRedirect(false), 500);
        return;
      }
      
      // CRITICAL FIX: Always clear the justLoggedIn flag to prevent multiple redirects
      sessionStorage.removeItem('justLoggedIn');
      
      // Record this redirect attempt
      sessionStorage.setItem('last_redirect_time', now.toString());
      
      // More robust check for premium subscription
      const isPremiumOrPro = 
        (user?.planLevel === 'premium' || user?.isEnterprise) || 
        (user?.subscriptionStatus?.currentPlanLevel === 'premium');
      
      // Only redirect if we're not already where we should be
      if (isPremiumOrPro && !location.pathname.startsWith('/app')) {
        console.log('Redirecting premium user to dashboard...');
        setPendingRedirect(true);
        navigate('/app/', { replace: true });
        setTimeout(() => {
          setPendingRedirect(false);
          // Clear Google sign-in flag after successful navigation
          sessionStorage.removeItem('googleSignInInProgress');
          // Clear Google sign-up flag after successful navigation
          sessionStorage.removeItem('googleSignUpInProgress');
        }, 500);
      } else if (!isPremiumOrPro && !isEnterpriseUser && location.pathname !== '/pricing' && !location.pathname.startsWith('/success')) {
        // IMPORTANT: Only redirect non-enterprise basic users to public pricing
        // Enterprise users NEVER go to pricing page
        console.log('Redirecting basic non-enterprise user to public pricing...');
        navigate('/pricing', { replace: true });
        // CRITICAL FIX: The redirect is initiated, so we MUST clear the pending state.
        setPendingRedirect(false);
        // Clear Google sign-in flags immediately
        sessionStorage.removeItem('googleSignInInProgress');
        sessionStorage.removeItem('googleSignUpInProgress');
      } else if (isEnterpriseUser && !isPremiumOrPro && !location.pathname.startsWith('/app')) {
        // Enterprise users without premium/pro plans still go to app, not pricing
        console.log('Redirecting enterprise user to app...');
        setPendingRedirect(true);
        navigate('/app/', { replace: true });
        setTimeout(() => {
          setPendingRedirect(false);
          sessionStorage.removeItem('googleSignInInProgress');
          sessionStorage.removeItem('googleSignUpInProgress');
        }, 500);
      } else {
        console.log('NavigationHandler: User already at correct destination, no redirect needed');
        // Clear Google sign-in flag if we're already at the right place
        sessionStorage.removeItem('googleSignInInProgress');
        sessionStorage.removeItem('googleSignUpInProgress');
      }
    }
  }, [user, location.pathname, navigate]);
  
  // If we're in a pending redirect state, or email verification is in progress, show loading to prevent flashes
  if (pendingRedirect) {
    return <AuthLoadingScreen />;
  }
  const evInProgress = sessionStorage.getItem('email_verification_in_progress') === 'true';
  if (evInProgress) {
    return <AuthLoadingScreen />;
  }
  
  // Special handling for sign out process
  const isSigningOut = sessionStorage.getItem('isSigningOut') === 'true';
  if (isSigningOut && location.pathname === '/') {
    return <AuthLoadingScreen />;
  }
  
  return null;
}

export default NavigationHandler;

